import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { PMDialog } from "../../utils/PMDialog";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import {
  CheckCircleIcon,
  DocumentTextIcon,
  UserIcon,
  TagIcon,
  PlusIcon,
  TrashIcon,
  LinkIcon,
} from "@heroicons/react/24/outline";
import { fetchNotes, createNote } from "../../../api/notes";
import { CustomerSelector } from "../../utils/moduleSelectors/customerSelector";
import { customerLockBlocksSubmit } from "../../../contexts/CustomerLockTracker";
import { CaseSelector } from "../../utils/moduleSelectors/caseSelector";
import { fetchCaseTypes } from "../../../api/case/type";
import {
  createCaseFromCall,
  linkCallToCase,
  processCallWithoutCase,
} from "../../../api/case";
import { fetchUsers } from "../../../api/user";
import PMCombobox from "../../utils/PMCombobox";
import { PMField } from "../../utils/PMField";
import { priorityOptions } from "../../dashboard/quality/deviations/utils";
import { fetchCases } from "../../../api/case";
import { last8Digits } from "../../Utils";

const CreateCaseDialog = ({
  open,
  onClose,
  call,
  onCaseCreated,
  onCallProcessed,
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [notes, setNotes] = useState([]);
  const [selectedNoteIds, setSelectedNoteIds] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedCase, setSelectedCase] = useState(null);
  const [caseTypes, setCaseTypes] = useState([]);
  const [selectedCaseType, setSelectedCaseType] = useState(null);
  const [showCustomerSelector, setShowCustomerSelector] = useState(false);
  const [showCaseSelector, setShowCaseSelector] = useState(false);
  const [customerError, setCustomerError] = useState("");
  const [userOptions, setUserOptions] = useState({
    isLoading: true,
    error: false,
    errors: [],
    data: [],
  });
  const [caseTypeOptions, setCaseTypeOptions] = useState({
    isLoading: true,
    error: false,
    errors: [],
    data: [],
  });
  const [inputFields, setInputFields] = useState([]);
  const [showNewFieldForm, setShowNewFieldForm] = useState(false);
  const [newField, setNewField] = useState({ name: "", value: "" });
  const [linkDescription, setLinkDescription] = useState("");

  // Default input fields from the Ruby model
  const defaultInputFields = [
    { name: "name", value: "name" },
    { name: "email", value: "email" },
    { name: "mobile", value: "mobile" },
    { name: "address", value: "address" },
    { name: "address2", value: "address2" },
    { name: "postal", value: "postal" },
    { name: "city", value: "city" },
    { name: "model_name", value: "model_name" },
    { name: "model_year", value: "model_year" },
    { name: "serial_number", value: "serial_number" },
  ];

  useEffect(() => {
    if (open && call) {
      fetchCallNotes();
      fetchCaseTypesList();
      fetchAndMapUsers();
    }
  }, [open, call]);

  const fetchCallNotes = async () => {
    setIsLoading(true);
    try {
      const controller = {
        filters: [{ name: "phone", value: last8Digits(call.from_number) }],
        page: 1,
        per_page: 20,
        sort: "desc",
        sortBy: "updated_at",
        view: "all",
      };
      const res = await fetchNotes(controller);
      setNotes(res.data.data || []);
    } catch (err) {
      toast.error(t("Failed to load notes"));
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCaseTypesList = async () => {
    try {
      const controller = {
        page: 1,
        per_page: 100,
        sort: "asc",
        sortBy: "title",
        view: "all",
      };
      const res = await fetchCaseTypes(controller);
      setCaseTypes(res.data.data || []);

      // Map case types to options format
      const array = [];
      if (res.data) {
        res.data.map((row, i) => {
          const rowParams = {
            value: row.id,
            title: row.title,
            order: i,
          };
          array.push(rowParams);
        });
        setCaseTypeOptions((prevState) => ({
          ...prevState,
          isLoading: false,
          data: array,
        }));
      }
    } catch (err) {
      toast.error(t("Failed to load case types"));
    }
  };

  const fetchAndMapUsers = async () => {
    setUserOptions((prevState) => ({
      ...prevState,
      isLoading: true,
    }));

    const controller = {
      filters: [{ name: "role", value: "case" }],
      page: 1,
      query: "",
      sort: "desc",
      sortBy: "id",
      view: "all",
      fields: "id,name,displayTitle",
    };

    try {
      const res = await fetchUsers(controller);
      if (res.data) {
        const array = [];
        if (!res.data) return array;
        res.data.map((row, i) => {
          const rowParams = {
            value: row.id,
            title: row.name,
            order: i,
          };
          array.push(rowParams);
        });
        setUserOptions((prevState) => ({
          ...prevState,
          isLoading: false,
          data: array,
        }));
      }
    } catch (err) {
      setUserOptions((prevState) => ({
        ...prevState,
        isLoading: false,
        error: true,
        errors: null,
      }));
    }
  };

  const handleNoteSelect = (noteId) => {
    setSelectedNoteIds((prev) =>
      prev.includes(noteId)
        ? prev.filter((id) => id !== noteId)
        : [...prev, noteId]
    );
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setAddingNote(true);
    try {
      const payload = {
        note: newNote,
        module: "phone",
        module_param: last8Digits(call.from_number),
      };
      const res = await createNote(payload);
      setNotes([res.data.data, ...notes]);
      setNewNote("");
      toast.success(t("Note added"));
    } catch (err) {
      console.log("Error adding note:", err);
      toast.error(t("Failed to add note"));
    } finally {
      setAddingNote(false);
    }
  };

  const handleAddInputField = () => {
    if (!newField.name.trim() || !newField.value.trim()) {
      toast.error(t("Both name and value are required"));
      return;
    }

    setInputFields([...inputFields, { ...newField, id: Date.now() }]);
    setNewField({ name: "", value: "" });
    setShowNewFieldForm(false);
  };

  const handleRemoveInputField = (index) => {
    setInputFields(inputFields.filter((_, i) => i !== index));
  };

  const handleSelectDefaultField = (field) => {
    // Check if field already exists
    const exists = inputFields.find((f) => f.name === field.name);
    if (exists) {
      toast.error(t("Field already added"));
      return;
    }

    setInputFields([...inputFields, { ...field, id: Date.now(), value: "" }]);
  };

  const handleCustomerChange = (data) => {
    if (data?.target?.data) {
      const customer = data.target.data;
      setSelectedCustomer(customer);
      setCustomerError("");
    }
  };

  const handleCaseChange = (data) => {
    if (data?.target?.data) {
      const caseData = data.target.data.object;

      setSelectedCase(caseData);
    }
  };

  const handleUnlinkCase = () => {
    setSelectedCase(null);
    setLinkDescription("");
  };

  const initialValues = {
    title: "",
    description: "",
    customer_id: null,
    case_type_id: null,
    user_assigned_id: null,
    priority: "1",
  };

  const validationSchema = useMemo(() => {
    if (selectedCase) {
      // When linking to existing case, no validation needed
      return Yup.object({});
    } else {
      // When creating new case, validate required fields
      return Yup.object({
        title: Yup.string().required(t("title.required")),
        description: Yup.string().required(t("Description is required")),
      });
    }
  }, [selectedCase, t]);

  const handleSubmit = async (values, helpers) => {


    setIsLoading(true);
    try {

      if (selectedCase) {
        // Link to existing case
        const response = await linkCallToCase({
          phonero_call_id: call?.call_id,
          case_id: selectedCase.id,
          note_ids: selectedNoteIds,
          new_note: newNote.trim() ? newNote : undefined,
          description: linkDescription.trim() || undefined,
        });


        if (response.success) {
          toast.success(t("Call successfully linked to case"));
          onCaseCreated(selectedCase);
          onClose();
        } else {
          toast.error(response.message || t("Failed to link call to case"));
        }
      } else {
        // Create new case
        const title =
          values.title.trim() ||
          `${t("case")}: ${call?.from_number || t("from")}`;
        const description =
          values.description.trim() ||
          `${t("Phone call received from")} ${call?.from_number || t("from")}`;

        const payload = {
          case: {
            title: title,
            description: description,
            module: "phone",
            module_param: last8Digits(call?.from_number),
            customer_id: selectedCustomer?.id,
            case_type_id: values.case_type_id,
            user_assigned_id: values.user_assigned_id,
            priority: values.priority,
            fields: inputFields.map((field) => ({
              name: field.name,
              value: field.value,
            })),
          },
          note_ids: selectedNoteIds,
          new_note: newNote.trim() ? newNote : undefined,
        };


        const result = await createCaseFromCall(payload);

        if (result.success && result.case) {
          toast.success(t("Case created successfully!"));
          onCaseCreated(result.case);
          onClose();
        } else {
          toast.error(result.message || t("Failed to create case"));
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      toast.error(t("Failed to create case"));
    } finally {
      setIsLoading(false);
      helpers.setSubmitting(false);
    }
  };

  const handleProcessWithoutAction = async () => {
    setIsLoading(true);
    try {
      const response = await processCallWithoutCase({
        phonero_call_id: call?.call_id,
        reason: t("No action required"),
      });
      if (response.success) {
        toast.success(t("Call marked as processed"));
        onClose();
        onCallProcessed();
      } else {
        toast.error(response.message || t("Failed to process call"));
      }
    } catch (error) {
      toast.error(t("Failed to process call"));
    } finally {
      setIsLoading(false);
    }
  };

  const getCaseDescription = (caseData) => {
    // Check multiple possible locations for description
    return (
      caseData?.description ||
      caseData?.item?.description ||
      caseData?.case_item?.description ||
      caseData?.case?.description ||
      null
    );
  };

  if (!call) return null;

  return (
    <PMDialog
      open={open}
      onClose={() => {
        onClose();
        setLinkDescription("");
      }}
      title={t("Create Case from Call")}
      size="3xl"
    >
      <div className="space-y-6">
        {/* Call Information */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-lg font-medium text-gray-900 mb-3">
            {t("Call Information")}
          </h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium">{t("from")}:</span>{" "}
              {call.from_number || t("Unknown")}
            </div>
            <div>
              <span className="font-medium">{t("to")}:</span>{" "}
              {call.to_number || t("Unknown")}
            </div>
            <div>
              <span className="font-medium">{t("Status")}:</span> {call.status}
            </div>
            <div>
              <span className="font-medium">{t("Duration")}:</span>{" "}
              {call.duration
                ? `${Math.floor(call.duration / 60)}m ${call.duration % 60}s`
                : t("Unknown")}
            </div>
            <div>
              <span className="font-medium">{t("Time")}:</span>{" "}
              {call.started_at
                ? new Date(call.started_at).toLocaleString()
                : t("Unknown")}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="flex space-x-3">
          <button
            onClick={handleProcessWithoutAction}
            disabled={isLoading}
            className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50"
          >
            <CheckCircleIcon className="h-4 w-4 mr-2" />
            {t("Process (No Action)")}
          </button>
        </div>

        {/* Notes Section */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {t("Call Notes")}
          </h3>
          {isLoading ? (
            <div className="flex justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <>
              {notes.length === 0 ? (
                <div className="text-center py-4 text-gray-500">
                  <DocumentTextIcon className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                  <p>{t("No notes found for this call")}</p>
                </div>
              ) : (
                <div className="space-y-3 mb-4">
                  {notes.map((note) => (
                    <div
                      key={note.id}
                      className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50"
                    >
                      <div className="flex items-start space-x-3">
                        <input
                          type="checkbox"
                          checked={selectedNoteIds.includes(note.id)}
                          onChange={() => handleNoteSelect(note.id)}
                          className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-gray-900">
                              {note.created_by?.name || t("Unknown User")}
                            </span>
                            <span className="text-xs text-gray-500">
                              {note.created_at
                                ? new Date(note.created_at).toLocaleString()
                                : ""}
                            </span>
                          </div>
                          <p className="text-sm text-gray-700 whitespace-pre-wrap">
                            {note.note}
                          </p>
                          {note.tags && note.tags.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1">
                              {note.tags.map((tag, idx) => (
                                <span
                                  key={idx}
                                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                                >
                                  {tag.module}: {tag.module_param}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Selected notes summary */}
              {selectedNoteIds.length > 0 && (
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center">
                    <CheckCircleIcon className="h-5 w-5 text-blue-600 mr-2" />
                    <span className="text-sm font-medium text-blue-900">
                      {t("Selected Notes")}: {selectedNoteIds.length}{" "}
                      {t("note(s) will be attached to the case")}
                    </span>
                  </div>
                </div>
              )}

              {/* Add new note section */}
              <div className="border-t border-gray-200 pt-4">
                <h4 className="text-sm font-medium text-gray-900 mb-2">
                  {t("Add New Note")}
                </h4>
                <div className="flex items-center space-x-2">
                  <textarea
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder={t("Add a note...")}
                    className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                    rows={2}
                    disabled={addingNote}
                  />
                  <button
                    type="button"
                    onClick={handleAddNote}
                    disabled={addingNote || !newNote.trim()}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {addingNote ? t("Adding...") : t("Add Note")}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Action Selection */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {t("Action")}
          </h3>

          {/* Link to Existing Case */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t("Link to Existing Case")}
            </label>
            {selectedCase ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div>
                    <div className="font-medium text-blue-900">
                      {selectedCase.displayTitle ||
                        `Case #${selectedCase.case_number}`}
                    </div>
                    <div className="text-sm text-blue-700">
                      {selectedCase.customer?.displayTitle}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleUnlinkCase}
                    className="text-red-600 hover:text-red-900"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>

                {/* Description field for linking */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("Description for this link")}
                  </label>
                  <textarea
                    value={linkDescription}
                    onChange={(e) => setLinkDescription(e.target.value)}
                    placeholder={t(
                      "Describe why you're linking this call to the case..."
                    )}
                    rows={3}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    {t(
                      "This description will be added to the case item that links the call"
                    )}
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <CaseSelector
                  fields="id,displayTitle,case_number,customer"
                  onChange={handleCaseChange}
                  label="Case"
                  searchQuery=""
                  showField={true}
                />
                <p className="text-xs text-gray-500">
                  {t(
                    "Select an existing case to link this call to, or leave empty to create a new case"
                  )}
                </p>
              </div>
            )}
          </div>

          {/* Action Summary */}
          {selectedCase && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center">
                <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
                <span className="text-sm font-medium text-green-900">
                  {t("Will link call to existing case")}:{" "}
                  {selectedCase.displayTitle ||
                    selectedCase.title ||
                    `Case #${selectedCase.case_number}`}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Selected Case Info */}
        {selectedCase && (
          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-blue-900 mb-2">
              Selected Case
            </h3>
            <div className="text-sm text-blue-800 space-y-2">
              <p>
                <strong>Case Number:</strong>{" "}
                {selectedCase.case_number || "N/A"}
              </p>
              <p>
                <strong>Title:</strong>{" "}
                {selectedCase.title || selectedCase.displayTitle || "N/A"}
              </p>
              <p>
                <strong>Status:</strong> {selectedCase.status || "N/A"}
              </p>
              {selectedCase.user_assigned && (
                <p>
                  <strong>Assigned to:</strong>{" "}
                  {selectedCase.user_assigned.name || "N/A"}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Case Creation Form - Show for both creating and linking */}
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {(formik) => (
            <Form className="space-y-6">
              {/* Case Details - Only show when creating new case */}
              {!selectedCase && (
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    {t("Case Details")}
                  </h3>
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label
                        htmlFor="title"
                        className="block text-sm font-medium text-gray-700"
                      >
                        {t("Title")}
                      </label>
                      <input
                        type="text"
                        id="title"
                        name="title"
                        value={formik.values.title}
                        onChange={formik.handleChange}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      />
                      {formik.errors.title && (
                        <p className="mt-1 text-sm text-red-600">
                          {formik.errors.title}
                        </p>
                      )}
                    </div>

                    {/* Customer Selection */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t("Customer")}
                      </label>

                      {/* Show possible customers from call data */}
                      {call?.possible_customers &&
                        call.possible_customers.length > 0 && (
                          <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                            <p className="text-sm font-medium text-blue-900 mb-2">
                              {t("Possible customers from call data")}:
                            </p>
                            <div className="space-y-1">
                              {call.possible_customers.map((customer, idx) => (
                                <button
                                  key={customer.id}
                                  type="button"
                                  onClick={() => {
                                    setSelectedCustomer(customer);
                                    setCustomerError("");
                                  }}
                                  className={`w-full text-left p-2 rounded border text-sm transition-colors ${
                                    selectedCustomer?.id === customer.id
                                      ? "bg-blue-100 border-blue-300 text-blue-900"
                                      : "bg-white border-gray-200 hover:bg-gray-50"
                                  }`}
                                >
                                  <div className="font-medium">
                                    {customer.name}
                                  </div>
                                  <div className="text-xs text-gray-600">
                                    #{customer.customer_number} -{" "}
                                    {t("Matched on")} {customer.match_type} (
                                    {customer.match_number})
                                  </div>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                      {/* Customer not found notice */}
                      {call?.possible_customers &&
                        call.possible_customers.length === 0 && (
                          <div className="mb-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                            <p className="text-sm text-yellow-800">
                              {t(
                                "No customer found for this phone number. You can create a case without a customer or select one manually."
                              )}
                            </p>
                          </div>
                        )}

                      <div className="flex items-center space-x-2">
                        <button
                          type="button"
                          onClick={() => setShowCustomerSelector(true)}
                          className={`flex-1 flex items-center justify-between px-3 py-2 border rounded-md shadow-sm bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                            customerError ? "border-red-300" : "border-gray-300"
                          }`}
                        >
                          <span
                            className={
                              selectedCustomer
                                ? "text-gray-900"
                                : "text-gray-500"
                            }
                          >
                            {selectedCustomer
                              ? selectedCustomer.displayTitle
                              : t("Select customer (optional)...")}
                          </span>
                          <UserIcon className="h-4 w-4 text-gray-400" />
                        </button>
                      </div>
                      {customerError && (
                        <p className="mt-1 text-sm text-red-600">
                          {customerError}
                        </p>
                      )}
                    </div>

                    {/* Case Type Selection */}
                    <div>
                      <PMCombobox
                        name="case_type_id"
                        label={t("Case Type")}
                        options={caseTypeOptions.data}
                        {...formik}
                      />
                    </div>

                    {/* Priority Selection */}
                    <div>
                      <PMCombobox
                        name="priority"
                        label={t("Priority")}
                        options={priorityOptions}
                        {...formik}
                      />
                    </div>

                    {/* User Assigned Selection */}
                    <div>
                      <PMCombobox
                        name="user_assigned_id"
                        label={t("User Assigned")}
                        options={userOptions.data}
                        {...formik}
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="description"
                        className="block text-sm font-medium text-gray-700"
                      >
                        {t("Description")}
                      </label>
                      <textarea
                        id="description"
                        name="description"
                        rows={3}
                        value={formik.values.description}
                        onChange={formik.handleChange}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      />
                      {formik.errors.description && (
                        <p className="mt-1 text-sm text-red-600">
                          {formik.errors.description}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Input Fields Section - Only show when creating new case */}
              {!selectedCase && (
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    {t("Input Fields")}
                  </h3>

                  {/* Default Fields Selector */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t("Add Default Field")}
                    </label>
                    <select
                      onChange={(e) => {
                        const selectedField = defaultInputFields.find(
                          (f) => f.name === e.target.value
                        );
                        if (selectedField) {
                          handleSelectDefaultField(selectedField);
                        }
                      }}
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      defaultValue=""
                    >
                      <option value="">{t("Select a default field...")}</option>
                      {defaultInputFields.map((field) => (
                        <option key={field.name} value={field.name}>
                          {t(field.name)}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* New Field Form */}
                  <div className="mb-4">
                    <button
                      type="button"
                      onClick={() => setShowNewFieldForm(!showNewFieldForm)}
                      className="flex items-center px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <PlusIcon className="h-4 w-4 mr-2" />
                      {t("Add Custom Field")}
                    </button>

                    {showNewFieldForm && (
                      <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">
                              {t("Field Name")}
                            </label>
                            <input
                              type="text"
                              value={newField.name}
                              onChange={(e) =>
                                setNewField({
                                  ...newField,
                                  name: e.target.value,
                                })
                              }
                              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                              placeholder={t("Enter field name")}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">
                              {t("Field Value")}
                            </label>
                            <input
                              type="text"
                              value={newField.value}
                              onChange={(e) =>
                                setNewField({
                                  ...newField,
                                  value: e.target.value,
                                })
                              }
                              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                              placeholder={t("Enter field value")}
                            />
                          </div>
                        </div>
                        <div className="mt-3 flex space-x-2">
                          <button
                            type="button"
                            onClick={handleAddInputField}
                            className="px-3 py-1 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                          >
                            {t("Add Field")}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setShowNewFieldForm(false);
                              setNewField({ name: "", value: "" });
                            }}
                            className="px-3 py-1 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                          >
                            {t("Cancel")}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Input Fields Table */}
                  {inputFields.length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-sm font-medium text-gray-900 mb-2">
                        {t("Added Fields")}
                      </h4>
                      <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                        <table className="min-w-full divide-y divide-gray-300">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                {t("Name")}
                              </th>
                              <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                {t("Value")}
                              </th>
                              <th className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                                <span className="sr-only">{t("Actions")}</span>
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200 bg-white">
                            {inputFields.map((field, index) => (
                              <tr key={field.id}>
                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                                  {t(field.name)}
                                </td>
                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                  <input
                                    type="text"
                                    value={field.value}
                                    onChange={(e) => {
                                      const updatedFields = [...inputFields];
                                      updatedFields[index].value =
                                        e.target.value;
                                      setInputFields(updatedFields);
                                    }}
                                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
                                    placeholder={t("Enter value")}
                                  />
                                </td>
                                <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleRemoveInputField(index)
                                    }
                                    className="text-red-600 hover:text-red-900"
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  {t("Cancel")}
                </button>
                <button
                  type="submit"
                  disabled={
                    isLoading ||
                    formik.isSubmitting ||
                    customerLockBlocksSubmit(formik.values)
                  }
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {isLoading || formik.isSubmitting
                    ? selectedCase
                      ? t("Linking...")
                      : t("Creating...")
                    : selectedCase
                    ? t("Link to Case")
                    : t("Create Case")}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>

      {/* Customer Selector Dialog */}
      <CustomerSelector
        showField={false}
        open={showCustomerSelector}
        onClose={(state) => {
          setShowCustomerSelector(false);
        }}
        onChange={handleCustomerChange}
      />
    </PMDialog>
  );
};

export default CreateCaseDialog;
